import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import '../EditTablePage/styles.css';
import { showSuccess } from '../../utils/toast';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const AdminEditTablePage = () => {
  const navigate = useNavigate();
  const { id: entityId, schemaId } = useParams();
  const [schema, setSchema] = useState(null);
  const [newFields, setNewFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const dataTypes = [
    { value: 'text', label: 'Text' },
    { value: 'integer', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'email', label: 'Email' }
  ];

  useEffect(() => {
    if (schemaId) {
      fetchSchema();
    }
  }, [schemaId]);

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      // Include entity_id query parameter for admin mode
      const url = entityId
        ? `${API_URL}/schemas/${schemaId}?entity_id=${entityId}`
        : `${API_URL}/schemas/${schemaId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const data = await response.json();
      setSchema(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addNewField = () => {
    setNewFields([...newFields, { name: '', type: 'text', required: false }]);
  };

  const removeNewField = (index) => {
    const updatedFields = newFields.filter((_, i) => i !== index);
    setNewFields(updatedFields);
  };

  const updateNewField = (index, field, value) => {
    const updatedFields = [...newFields];
    updatedFields[index][field] = value;
    setNewFields(updatedFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newFields.length === 0) {
      setError('Please add at least one new field');
      return;
    }

    for (let i = 0; i < newFields.length; i++) {
      const field = newFields[i];
      if (!field.name.trim()) {
        setError(`Field ${i + 1} name is required`);
        return;
      }
    }

    const fieldNames = newFields.map(field => field.name.toLowerCase());
    const uniqueNames = new Set(fieldNames);
    if (uniqueNames.size !== fieldNames.length) {
      setError('Field names must be unique');
      return;
    }

    const existingColumns = schema.schema_definition.columns || [];
    const existingFieldNames = existingColumns.map(col => col.name.toLowerCase());

    for (const fieldName of fieldNames) {
      if (existingFieldNames.includes(fieldName)) {
        setError(`Field name '${fieldName}' already exists in the table`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/schemas/${schemaId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_fields: newFields.map(field => ({
            name: field.name.trim(),
            type: field.type,
            required: field.required
          })),
          entity_id: entityId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update schema');
      }

      const msg = `Successfully added ${data.data.added_fields.length} new field(s) to the table`;
      setSuccess(msg);
      showSuccess(msg);
      setNewFields([]);
      await fetchSchema();

      setTimeout(() => {
        navigate(`/admin/entities/${entityId}/tables`);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = () => {
    setConfirmText('');
    setConfirmOpen(true);
  };

  const closeDeleteModal = () => {
    if (!deleting) setConfirmOpen(false);
  };

  const handleDeleteTable = async () => {
    if (!schema?.schema_id) return;
    setError('');
    setSuccess('');
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${entityId}/schemas/${schema.schema_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete table');
      }
      setSuccess('Table deleted successfully');
      showSuccess('Table deleted successfully');
      setConfirmOpen(false);
      await fetchSchema();
      setTimeout(() => {
        navigate(`/admin/entities/${entityId}/tables`);
      }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-table-container">
        <div className="edit-table-loading">
          <div className="loading-spinner"></div>
          <p>Loading table schema...</p>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="edit-table-container">
        <div className="edit-table-error">
          <h3>Schema not found</h3>
          <p>The requested table schema could not be found.</p>
          <button
            onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
            className="back-to-tables-btn"
          >
            Back to Tables
          </button>
        </div>
      </div>
    );
  }

  const existingColumns = schema.schema_definition.columns || [];

  return (
    <SidebarLayout>
      <div className="edit-table-container">
        <div className="edit-table-header">
          <div className="edit-table-header-content">
            <div className="edit-table-heading">
              <h1>Edit Table: {schema.table_name}</h1>
              <p>Add new fields to the existing table</p>
            </div>

          </div>
          <div className="edit-table-actions">
            <button
              onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
              className="edit-table-back-btn"
            >
              Back to Tables
            </button>
            <button
              type="button"
              onClick={openDeleteModal}
              className="danger-outline-btn"
            >
              Delete Table
            </button>
          </div>
        </div>

        <div className="edit-table-content">
          <div className="existing-fields-section">
            <h2>Existing Fields</h2>
            <div className="table-info">
              <span className="version-info">Version {schema.version}</span>
              <span className="field-count">{existingColumns.length} existing fields</span>
            </div>
            <div className="existing-fields-list">
              {existingColumns.map((column, index) => (
                <div key={index} className="existing-field-item">
                  <div className="field-name">{column.name}</div>
                  <div className="field-type">{column.type}</div>
                  <div className="field-required">
                    {column.required ? 'Required' : 'Optional'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="new-fields-section">
            <div className="section-header">
              <h2>Add New Fields</h2>
              <button
                type="button"
                onClick={addNewField}
                className="add-field-btn"
              >
                + Add New Field
              </button>
            </div>

            {newFields.length === 0 ? (
              <div className="no-new-fields-message">
                <p>No new fields added yet. Click "Add New Field" to get started.</p>
              </div>
            ) : (
              <div className="new-fields-list">
                {newFields.map((field, index) => (
                  <div key={index} className="new-field-item">
                    <div className="field-inputs">
                      <div className="form-group">
                        <label>Field Name *</label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateNewField(index, 'name', e.target.value)}
                          placeholder="Enter field name"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Data Type *</label>
                        <select
                          value={field.type}
                          onChange={(e) => updateNewField(index, 'type', e.target.value)}
                          className="form-select"
                        >
                          {dataTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateNewField(index, 'required', e.target.checked)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">Required</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeNewField(index)}
                        className="remove-field-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="edit-table-form">
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || newFields.length === 0}
                className="submit-btn"
              >
                {submitting ? 'Adding Fields...' : 'Add Fields'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
              </div>
            )}
          </form>
        </div>
      </div>
      {/* </SidebarLayout> */}
      {confirmOpen && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete table "{schema?.table_name}"</h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">This action cannot be undone. This will permanently mark the table as deleted for this school.</p>
              <p className="modal-subtext">Please type <strong style={{ color: '#000', fontWeight: 'bold' }}>Delete {schema?.table_name}</strong> to confirm.</p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Type Delete ${schema?.table_name} to confirm`}
                className={`confirm-input ${confirmText === `Delete ${schema?.table_name}` ? 'valid' : confirmText ? 'invalid' : ''}`}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-btn" onClick={closeDeleteModal} disabled={deleting}>Cancel</button>
              <button
                type="button"
                className="danger-btn"
                disabled={deleting || confirmText !== `Delete ${schema?.table_name}`}
                onClick={handleDeleteTable}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </SidebarLayout>
  );
};

export default AdminEditTablePage;

